import React, { useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';

// ── QA bar color thresholds (mirrors SeoResultEntry) ─────────────────────
const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

// ── Paragraph + inline-list splitter (copied from SeoResultEntry) ───────
const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const splitTextAndLists = (text) => {
  if (!text) return [];
  const listRegex = /<(ul|ol)\b[^>]*>[\s\S]*?<\/\1>/gi;
  const blocks = [];
  let lastIdx = 0;
  let m;
  while ((m = listRegex.exec(text)) !== null) {
    if (m.index > lastIdx) blocks.push({ type: 'text', value: text.slice(lastIdx, m.index) });
    blocks.push({ type: 'list', value: m[0] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) blocks.push({ type: 'text', value: text.slice(lastIdx) });
  return blocks.length ? blocks : [{ type: 'text', value: text }];
};

const Paragraphs = ({ text }) => {
  const blocks = splitTextAndLists(text);
  return blocks.flatMap((block, bi) => {
    if (block.type === 'list') {
      return [
        <div
          key={`list-${bi}`}
          style={{ margin: '0 0 12px', lineHeight: 1.75, fontSize: '14px' }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.value) }}
        />,
      ];
    }
    return splitParagraphs(block.value).map((p, i) => (
      <p
        key={`p-${bi}-${i}`}
        style={{ margin: '0 0 12px', lineHeight: 1.75, fontSize: '14px' }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
      />
    ));
  });
};

// ── Sub-components ──────────────────────────────────────────────────────
const SectionLabel = ({ children, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const CopyBtn = ({ active, onClick, label, t, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '5px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
      background: 'transparent',
      color: color || t.textMuted || '#7a7a8e',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
    }}
  >
    {active ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> {label}</>}
  </button>
);

// ── Plain-text serializer for the key-takeaways Copy button ─────────────
// (Whole-article copy is provided by the wrapper card's "Copia tutto"
//  button in Step3History, which uses BE-built `c.full_text`. We don't
//  duplicate that here.)
const buildKeyTakeawaysText = (items) =>
  items.map((kt, i) => `${i + 1}. ${kt}`).join('\n');

// ── Main component ──────────────────────────────────────────────────────
const NewsletterResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  // Newsletter brand accent: LinkedIn blue (matches EMAIL_CHANNELS.linkedin-newsletter.color)
  const color = '#0A66C2';
  const eId = entry.id || 'newsletter';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;
  const sections = Array.isArray(c.sections) ? c.sections : [];
  const keyTakeaways = useMemo(
    () => (Array.isArray(c.key_takeaways) ? c.key_takeaways : []),
    [c.key_takeaways]
  );
  const companionPost = c.companion_post || '';
  const closingParagraph = c.closing_paragraph || '';
  const ctaUrl = c.cta_url || '';

  const ktText = useMemo(() => buildKeyTakeawaysText(keyTakeaways), [keyTakeaways]);

  return (
    <div>
      {/* Region 2 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 3 — Title + subtitle + Region 4 hook + Region 5 sections,
          wrapped in one card. Whole-article copy is handled by the
          "Copia tutto" button in the Step3History wrapper header. */}
      <div
        style={{
          padding: '18px 20px',
          background: w(0.02),
          border: `1px solid ${w(0.06)}`,
          borderRadius: '10px',
          marginBottom: '14px',
        }}
      >
        {/* Region 3 — Title */}
        {c.title && (
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              margin: '0 0 8px',
              lineHeight: 1.3,
              color: t.text,
            }}
          >
            {c.title}
          </h1>
        )}

        {/* Region 3 — Subtitle (optional) */}
        {c.subtitle && (
          <div
            style={{
              fontSize: '15px',
              fontStyle: 'italic',
              fontWeight: 400,
              color: t.textMuted || '#7a7a8e',
              marginBottom: '14px',
              lineHeight: 1.45,
            }}
          >
            {c.subtitle}
          </div>
        )}

        {/* Region 4 — Hook (introduction) */}
        {c.hook && (
          <div style={{ marginTop: c.subtitle ? '6px' : '14px', color: t.text }}>
            <Paragraphs text={c.hook} />
          </div>
        )}

        {/* Region 5 — Sections */}
        {sections.map((s, i) => (
          <div key={i} style={{ marginTop: '20px' }}>
            <h2
              id={s.heading_id || undefined}
              style={{
                fontSize: '17px',
                fontWeight: 700,
                color,
                margin: '0 0 10px',
                lineHeight: 1.35,
              }}
            >
              {s.h2}
            </h2>
            <div style={{ color: t.text }}>
              <Paragraphs text={s.content} />
            </div>
            {(s.subsections || []).map((sub, j) => (
              <div
                key={j}
                style={{
                  marginTop: '14px',
                  paddingLeft: '14px',
                  borderLeft: `2px solid ${color}30`,
                }}
              >
                <h3
                  style={{
                    fontSize: '14.5px',
                    fontWeight: 600,
                    color: t.textSoft || t.text,
                    margin: '0 0 8px',
                    lineHeight: 1.4,
                  }}
                >
                  {sub.h3}
                </h3>
                <div style={{ color: t.text }}>
                  <Paragraphs text={sub.content} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Region 6 — Key takeaways */}
      {keyTakeaways.length > 0 && (
        <div
          style={{
            marginBottom: '14px',
            padding: '14px 16px',
            background: `${color}08`,
            borderRadius: '10px',
            border: `1px solid ${color}20`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontFamily: "'JetBrains Mono', monospace",
                color,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              Punti chiave
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <CopyBtn
                active={copiedId === `nl-takeaways-${eId}`}
                onClick={() => onCopy?.(ktText, `nl-takeaways-${eId}`)}
                label="Copia"
                t={t}
                color={color}
              />
            </div>
          </div>
          <ol style={{ margin: 0, padding: '0 0 0 22px', color: t.text }}>
            {keyTakeaways.map((kt, i) => (
              <li
                key={i}
                style={{
                  fontSize: '13.5px',
                  lineHeight: 1.6,
                  marginBottom: i === keyTakeaways.length - 1 ? 0 : '6px',
                }}
              >
                {kt}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Region 7 — Closing paragraph + optional CTA button */}
      {closingParagraph && (
        <div
          style={{
            marginBottom: '14px',
            padding: '14px 16px',
            background: w(0.02),
            border: `1px solid ${w(0.06)}`,
            borderRadius: '10px',
          }}
        >
          <SectionLabel t={t}>Chiusura</SectionLabel>
          <div style={{ color: t.text }}>
            <Paragraphs text={closingParagraph} />
          </div>
          {ctaUrl && (
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '8px',
                padding: '8px 16px',
                background: `${color}15`,
                border: `1px solid ${color}40`,
                borderRadius: '8px',
                color,
                fontSize: '13px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Iscriviti alla newsletter →
            </a>
          )}
        </div>
      )}

      {/* Region 8 — Companion post */}
      {companionPost && (
        <div
          style={{
            marginBottom: '14px',
            padding: '16px',
            borderRadius: '10px',
            background: `${color}08`,
            border: `1px solid ${color}20`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '10px',
                fontFamily: "'JetBrains Mono', monospace",
                color,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontWeight: 700,
              }}
            >
              Post di accompagnamento
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <CopyBtn
                active={copiedId === `nl-companion-${eId}`}
                onClick={() => onCopy?.(companionPost, `nl-companion-${eId}`)}
                label="Copia"
                t={t}
                color={color}
              />
            </div>
          </div>
          <div
            style={{
              padding: '14px 16px',
              background: w(0.05),
              borderRadius: '8px',
              borderLeft: `3px solid ${color}`,
              fontSize: '14px',
              lineHeight: 1.7,
              color: t.text,
            }}
          >
            <Paragraphs text={companionPost} />
          </div>
        </div>
      )}

      {/* Region 9 — Sources cited (shared component) */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />

      {/* Word-count strip (FE choice — small footer) */}
      {c.word_count > 0 && (
        <div
          style={{
            marginTop: '12px',
            fontSize: '11.5px',
            fontFamily: "'JetBrains Mono', monospace",
            color: t.textMuted || '#7a7a8e',
          }}
        >
          {c.word_count} parole{sections.length > 0 ? ` • ${sections.length} sezion${sections.length === 1 ? 'e' : 'i'}` : ''}
        </div>
      )}
    </div>
  );
};

export default NewsletterResultEntry;
